import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import {
  getAgentsByUpdatedPresence,
  getSortedAgentsByAvailability,
} from 'dashboard/helper/agentHelper';
// NOTICE: KAPTION-INTEGRATION — smart-availability.UI.4
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import {
  decorateAgent,
  sortByAvailability,
} from 'dashboard/helper/kaption/smartAvailability';

/**
 * A composable function that provides a list of agents for assignment.
 *
 * @param {boolean} [includeNoneAgent=true] - Whether to include a 'None' agent option.
 * @returns {Object} An object containing the agents list and assignable agents.
 */
export function useAgentsList(includeNoneAgent = true) {
  const { t } = useI18n();
  const currentUser = useMapGetter('getCurrentUser');
  const currentChat = useMapGetter('getSelectedChat');
  const currentAccountId = useMapGetter('getCurrentAccountId');
  const assignable = useMapGetter('inboxAssignableAgents/getAssignableAgents');
  // NOTICE: KAPTION-INTEGRATION — smart-availability.UI.4
  // What the account's availability endpoint said about each person, when the flag is on
  // and the store has an answer for this inbox. Without either, the list below is
  // byte-identical to what it always was (smart-availability.UI.2).
  const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
  const smartAvailabilityFor = useMapGetter(
    'kaptionSmartAvailability/forAgent'
  );
  const smartAvailabilityHasData = useMapGetter(
    'kaptionSmartAvailability/hasData'
  );
  const smartAvailabilityShowCounts = useMapGetter(
    'kaptionSmartAvailability/showCounts'
  );

  const inboxId = computed(() => currentChat.value?.inbox_id);
  const isAgentSelected = computed(() => currentChat.value?.meta?.assignee);

  const smartAvailabilityActive = computed(
    () =>
      !!inboxId.value &&
      isKaptionFlagEnabled('KAPTION_SMART_AVAILABILITY') &&
      !!smartAvailabilityHasData.value?.(inboxId.value)
  );

  /**
   * Creates a 'None' agent object
   * @returns {Object} None agent object
   */
  const createNoneAgent = () => ({
    confirmed: true,
    name: t('AGENT_MGMT.MULTI_SELECTOR.LIST.NONE') || 'None',
    id: 0,
    role: 'agent',
    account_id: 0,
    email: 'None',
  });

  /**
   * @type {import('vue').ComputedRef<Array>}
   */
  const assignableAgents = computed(() => {
    return inboxId.value ? assignable.value(inboxId.value) : [];
  });

  // smart-availability.UI.3 / .UI.4 — available first (fewest open first), then at
  // capacity, then the rest; every row carries the word and, when allowed, the queue.
  const withSmartAvailability = agents => {
    const infoFor = id => smartAvailabilityFor.value?.(inboxId.value, id);
    const showCounts = !!smartAvailabilityShowCounts.value?.(inboxId.value);
    return sortByAvailability(agents, infoFor).map(agent =>
      decorateAgent(agent, infoFor(agent.id), t, showCounts)
    );
  };

  /**
   * @type {import('vue').ComputedRef<Array>}
   */
  const agentsList = computed(() => {
    const agents = assignableAgents.value || [];
    const agentsByUpdatedPresence = getAgentsByUpdatedPresence(
      agents,
      currentUser.value,
      currentAccountId.value
    );

    const filteredAgentsByAvailability = getSortedAgentsByAvailability(
      agentsByUpdatedPresence
    );

    const ordered = smartAvailabilityActive.value
      ? withSmartAvailability(filteredAgentsByAvailability)
      : filteredAgentsByAvailability;

    return [
      ...(includeNoneAgent && isAgentSelected.value ? [createNoneAgent()] : []),
      ...ordered,
    ];
  });

  return {
    agentsList,
    assignableAgents,
    smartAvailabilityActive,
  };
}
